import type { SagaIterator } from 'redux-saga';
import { call, delay, put, select, spawn, takeEvery } from 'redux-saga/effects';

import { accountsSelectors } from 'app-components/flux/accounts';
import { dateSelectors } from 'app-components/flux/date';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { transactionsActions, transactionsSelectors } from 'app-components/flux/transactions';
import type { TransactionRecord } from 'app-components/flux/transactions/transactionsTypes';
import { preProcess } from 'app-components/flux/transactions-processing';
import { getLogger } from 'app-components/utils/core';
import type RootState from 'app-components/utils/redux-store/rootState';

import { toMatchableAccounts } from './merchantOrderAccounts';
import { isSplitCandidate } from './merchantOrderCandidate';
import type { MerchantOrderMatch } from './merchantOrderMatch';
import { processMerchantOrders } from './merchantOrderProcessor';
import * as merchantOrdersActions from './merchantOrdersActions';
import { merchantOrdersApi } from './merchantOrdersApi';
import type { MerchantOrder } from './merchantOrdersTypes';
import { deferredWrite, type ExtraAction, getSalesTaxCoa, patchTransactionForMatch } from './merchantOrdersUtils';

const log = getLogger('flux/merchantOrders/merchantOrdersSagas');

/**
 * Mirrors the web and mobile senders, which do `setTimeout(() => dispatch(item.action), item.delay)`.
 * Spawned rather than forked so a delay cannot hold the saga — and with it the watcher's next
 * `take` — open while it waits.
 */
export function* dispatchExtraAction({ action, delay: delayMs }: ExtraAction): SagaIterator {
  if (delayMs) {
    yield delay(delayMs);
  }
  yield put(action);
}

/**
 * Fetches and validates merchant orders before handing them to the register processor.
 */
export function* fetchMerchantOrdersSaga(): SagaIterator {
  const enabled = yield select(featureFlagsSelectors.getFeatureFlag, 'retailPurchaseCategorizationRelease');
  if (!enabled) {
    return;
  }

  let orders: MerchantOrder[];
  try {
    orders = yield call(merchantOrdersApi.fetchAllOrders);
  } catch (error) {
    // A merchant-orders outage must never break sync.
    log.debug('merchant orders fetch failed; skipping this pass', error);
    return;
  }
  if (orders.length === 0) {
    return;
  }

  yield spawn(processMerchantOrdersSaga, orders);
}

/** Applies fetched merchant orders to the current register state. */
export function* processMerchantOrdersSaga(orders: MerchantOrder[]): SagaIterator {
  // The post-refresh transaction response completes before this worker starts, so matching and
  // writing use the refreshed register state rather than a snapshot from before sync.
  const state: RootState = yield select((rootState: RootState) => rootState);
  // getTransactionsById (not getResourcesById) filters to txnTypesDefault, which drops rows the
  // register itself hides — matching against those would produce matches the user cannot verify.
  const transactions = transactionsSelectors.getTransactionsById(state).valueSeq().toArray();
  const today = dateSelectors.getCurrentDateString(state);
  const accounts = toMatchableAccounts(accountsSelectors.getResourcesById(state).valueSeq().toArray());
  const result = processMerchantOrders(orders, transactions, { accounts, today, salesTaxCoa: getSalesTaxCoa(state) });

  // The per-reason skip counts are the point of the first pass: a payee-pattern or date-window bug
  // shows up here as a spike in one reason.
  log.debug('merchant order processing stats', result.stats);
  result.matches.forEach((match) => {
    log.debug(
      `merchant order ${match.candidate.order.id} matched transaction ${match.transactionId} ` +
        `(${isSplitCandidate(match.candidate) ? 'split' : 'categorization'})`,
    );
  });

  if (result.matches.length === 0) {
    return;
  }

  const candidates: Array<{ match: MerchantOrderMatch; txn: TransactionRecord }> = [];
  result.matches.forEach((match) => {
    const existing = transactionsSelectors.getTransactionById(state, match.transactionId);
    if (!existing) {
      return;
    }
    candidates.push({ match, txn: patchTransactionForMatch(existing, match.candidate) });
  });

  if (candidates.length === 0) {
    return;
  }

  const txnsByAccountId = transactionsSelectors.getTransactionsByAccountId(state);
  const featureFlags = featureFlagsSelectors.getFeatureFlags(state);
  const pack = preProcess.preProcessUpdate({
    txns: candidates.map(({ txn }) => txn),
    txnsByAccountId,
    featureFlags,
    state,
  });

  // preProcessUpdate drops a transaction from the pack when it records an error for it, so the
  // orders we consume have to come from what survived, not from what we asked it to write.
  // Deleting an order whose transaction was never written loses it with no way to notice.
  const packedIds = new Set(pack.allTxnsToUpdate.map((txn) => txn.id));
  const appliedMatches = candidates.filter(({ txn }) => packedIds.has(txn.id)).map(({ match }) => match);

  if (pack.errors.length > 0) {
    log.debug('preProcessUpdate rejected merchant order matches', pack.errors);
  }
  if (appliedMatches.length === 0) {
    return;
  }

  yield call(applyMerchantOrderMatchesSaga, pack, appliedMatches);
}

/**
 * Writes the pack, then consumes the orders it covered.
 *
 * Split from the matching half so the decision (which order belongs to which transaction) can be
 * tested without a store write, and so the write ordering lives in one place: write, confirm, then
 * delete. Never delete optimistically — an orphaned order on failure is recoverable, a duplicated
 * split is not.
 */
export function* applyMerchantOrderMatchesSaga(
  pack: ReturnType<typeof preProcess.preProcessUpdate>,
  appliedMatches: MerchantOrderMatch[],
): SagaIterator {
  const { settled, resolve, reject } = deferredWrite();
  yield put(
    transactionsActions.batchTransactions([...pack.allTxnsToDelete, ...pack.allTxnsToUpdate, ...pack.allTxnsToCreate], {
      context: 'merchantOrders',
      resolve,
      reject,
    }),
  );

  try {
    yield call(() => settled);
  } catch (error) {
    log.debug('merchant orders batch write failed; not deleting the matched orders', error);
    return;
  }

  for (const extraAction of pack.extraActions) {
    yield spawn(dispatchExtraAction, extraAction);
  }

  for (const match of appliedMatches) {
    const { order } = match.candidate;
    if (!order.id) {
      continue;
    }
    // The only record of what we matched — the delete destroys it.
    log.debug('deleting consumed merchant order', order);
    try {
      yield call(merchantOrdersApi.deleteOrder, order.id);
    } catch (error) {
      log.debug('deleteOrder failed', order.id, error);
    }
  }
}

export function* fetchAndProcessMerchantOrdersWatcher(): SagaIterator {
  yield takeEvery(merchantOrdersActions.fetchAndProcessMerchantOrders, fetchMerchantOrdersSaga);
}

export default [fetchAndProcessMerchantOrdersWatcher];
